import { useRef, type ReactNode } from 'react';
import { apiClient, type ApiClient } from './api/client';
import { AccountBar } from './features/account/AccountBar';
import { AccountDialogs } from './features/account/AccountDialogs';
import { AccountNotice } from './features/account/AccountNotice';
import { AccountProvider } from './features/account/AccountProvider';
import { DownloadApp } from './features/downloads/DownloadApp';
import { FeatureGuides } from './features/guides/FeatureGuides';
import { HomePage } from './features/home/HomePage';
import { PricingPage } from './features/pricing/PricingPage';
import type { openPaddleCheckout } from './features/account/paddle-checkout';
import { RewriteWorkspace } from './features/rewrite/RewriteWorkspace';
import { ConsentProvider } from './features/tracking/ConsentProvider';
import { TrackingProvider } from './features/tracking/TrackingProvider';
import { useArrival } from './hooks/useArrival';
import { useI18n } from './i18n/i18n-context';
import { I18nProvider, type LoadedLocale } from './i18n/I18nProvider';
import { AppShell } from './layout/AppShell';
import { useAppView } from './layout/useAppView';

interface AppProps {
  readonly client?: ApiClient;
  /** Leaves the app for checkout. */
  readonly navigate?: (url: string) => void;
  /** Opens Paddle's checkout for the transaction in the address. */
  readonly openCheckout?: typeof openPaddleCheckout;
  /** The interface's language, loaded before the first render; English by default. */
  readonly i18n?: LoadedLocale;
}

export function App({ client = apiClient, navigate, openCheckout, i18n }: AppProps) {
  const view = useAppView();
  return (
    <I18nProvider initial={i18n}>
      <ConsentProvider client={client}>
        <TrackingProvider client={client} view={view}>
          <AccountProvider client={client} navigate={navigate} openCheckout={openCheckout}>
            <AppShell view={view} actions={<AccountBar />}>
              <AccountNotice />
              {view === 'home' && (
                <div className="app-view app-view--home">
                  <HomePage />
                </div>
              )}
              {/* Keep the editor mounted so navigating preserves the draft, results, and undo. */}
              <StudioView shown={view === 'write'}>
                <PageIntro />
                <RewriteWorkspace client={client} active={view === 'write'} />
              </StudioView>
              {view === 'pricing' && (
                <div className="app-view app-view--pricing">
                  <PricingPage />
                </div>
              )}
              {view === 'desktop' && (
                <div className="app-view app-view--desktop">
                  <DownloadApp headingLevel={1} />
                </div>
              )}
              {view === 'guide' && (
                <div className="app-view app-view--guide">
                  <FeatureGuides headingLevel={1} />
                </div>
              )}
            </AppShell>
            <AccountDialogs />
          </AccountProvider>
        </TrackingProvider>
      </ConsentProvider>
    </I18nProvider>
  );
}

/** How long the Studio's entrance takes as it opens: its last parts have landed by then. */
const STUDIO_ARRIVAL = 1400;

/** The Studio, hidden while another page is open, and arriving each time it's shown. */
function StudioView({
  shown,
  children,
}: {
  readonly shown: boolean;
  readonly children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useArrival(ref, shown, STUDIO_ARRIVAL);
  return (
    <div ref={ref} className="app-view app-view--write" hidden={!shown} inert={!shown}>
      {children}
    </div>
  );
}

function PageIntro() {
  const { t } = useI18n();
  return (
    <div className="page-intro">
      <div className="page-title">
        <h1>{t.app.title}</h1>
      </div>
      <p className="page-description">{t.app.description}</p>
    </div>
  );
}
